import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/** Polar waveform — circular oscilloscope. */
export const radialWaveViz: Visualizer = {
  id: 'radial-wave',
  label: 'Radial Wave',
  draw(frame) {
    const { ctx, w, h, wave, freq, bands, t } = frame;
    clearBg(ctx, w, h, 0.4);
    const cx = w / 2;
    const cy = h / 2;
    const base = Math.min(w, h) * (0.18 + bands.bass * 0.04);

    const vg = ctx.createRadialGradient(cx, cy, base * 0.2, cx, cy, Math.min(w, h) * 0.5);
    vg.addColorStop(0, 'rgba(8, 22, 28, 0.5)');
    vg.addColorStop(1, 'rgba(3, 6, 10, 0.98)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);

    // frequency petals under wave
    const petals = 48;
    for (let i = 0; i < petals; i++) {
      const a = (i / petals) * Math.PI * 2 + t * 0.2;
      const v = freq[Math.floor((i / petals) * freq.length)] / 255;
      const r0 = base * 0.85;
      const r1 = base * 0.85 + v * Math.min(w, h) * 0.14;
      ctx.strokeStyle = `hsla(${160 + i * 3}, 90%, 55%, ${0.15 + v * 0.45})`;
      ctx.lineWidth = 2 + v * 3;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
      ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.stroke();
    }

    // outer waveform ring
    const layers = [
      { scale: 1, alpha: 0.9, hue: 165, lw: 2.2 },
      { scale: 1.12, alpha: 0.35, hue: 190, lw: 1.4 },
      { scale: 0.88, alpha: 0.4, hue: 145, lw: 1.2 },
    ];
    for (const L of layers) {
      ctx.beginPath();
      const n = Math.min(wave.length, 512);
      for (let i = 0; i <= n; i++) {
        const u = i / n;
        const a = u * Math.PI * 2 - Math.PI / 2 + t * 0.3 * bands.mids;
        const s = wave[i % wave.length] || 0;
        const r = (base + s * base * (1.8 + bands.energy) * L.scale) * (1 + bands.kick * 0.05);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = `hsla(${L.hue + bands.highs * 30}, 95%, 62%, ${L.alpha})`;
      ctx.lineWidth = L.lw + bands.beat;
      ctx.shadowColor = `hsla(${L.hue}, 100%, 55%, 0.5)`;
      ctx.shadowBlur = 12 + bands.energy * 10;
      ctx.stroke();
    }
    ctx.shadowBlur = 0;

    // core
    const core = ctx.createRadialGradient(cx, cy, 2, cx, cy, base * 0.5);
    core.addColorStop(0, `rgba(255,255,255,${0.35 + bands.beat * 0.4})`);
    core.addColorStop(0.4, `rgba(86,240,200,${0.2 + bands.energy * 0.25})`);
    core.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cy, base * 0.55, 0, Math.PI * 2);
    ctx.fill();

    drawHud(frame);
  },
};
